/**
 * GovCostAnalysisActualsPanel — post-award feedback loop.
 * Shown when the cost analysis is linked to a construction job.
 * Imports actuals from gov_construction_jobs + gov_pay_applications,
 * compares to the finalized estimate, computes variance.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { S, GOV_COLORS, fmtMoney, fmtMoneyFull } from './govShared';

interface Props {
  versionId: string;
  constructionJobId: string;
  expected: number;
  low: number;
  high: number;
}

export default function GovCostAnalysisActualsPanel({ versionId, constructionJobId, expected, low, high }: Props) {
  const [actuals, setActuals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const fetchActuals = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/gov/cost-analysis-versions/${versionId}/actuals`);
      setActuals(data?.actuals || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [versionId]);

  useEffect(() => { fetchActuals(); }, [fetchActuals]);

  const importActuals = async () => {
    setImporting(true);
    try {
      await api.post(`/gov/cost-analysis-versions/${versionId}/import-actuals`, { construction_job_id: constructionJobId });
      await fetchActuals();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Import failed');
    } finally { setImporting(false); }
  };

  const latest = actuals[0];
  const varianceColor = latest && latest.variance_amount != null
    ? (Math.abs(Number(latest.variance_pct || 0)) < 10 ? GOV_COLORS.success
       : Math.abs(Number(latest.variance_pct || 0)) < 20 ? GOV_COLORS.warning
       : GOV_COLORS.danger)
    : GOV_COLORS.textMuted;

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ ...S.panelTitle, marginBottom: 0 }}>📈 Actuals vs. Estimate</h3>
        <div style={{ flex: 1 }} />
        <button onClick={importActuals} disabled={importing} style={{ ...S.addBtn, padding: '6px 14px', fontSize: 12 }}>
          {importing ? 'Importing…' : '🔄 Import from Construction Job'}
        </button>
      </div>

      {loading ? (
        <div style={S.loading}>Loading…</div>
      ) : actuals.length === 0 ? (
        <div style={{ ...S.empty, padding: 20 }}>
          No actuals imported yet. Click "Import from Construction Job" to pull actual costs from the linked job.
        </div>
      ) : (
        <>
          {/* Latest actuals row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            <div>
              <div style={S.kvLabel}>Actual Total Cost</div>
              <div style={{ color: '#fff', fontSize: 20, fontWeight: 800, marginTop: 4 }}>{fmtMoney(latest.actual_total_cost)}</div>
            </div>
            <div>
              <div style={S.kvLabel}>Actual Revenue</div>
              <div style={{ color: '#fff', fontSize: 20, fontWeight: 800, marginTop: 4 }}>{fmtMoney(latest.actual_revenue)}</div>
            </div>
            <div>
              <div style={S.kvLabel}>Actual Margin</div>
              <div style={{ color: (latest.actual_gross_margin || 0) >= 0 ? GOV_COLORS.success : GOV_COLORS.danger, fontSize: 20, fontWeight: 800, marginTop: 4 }}>
                {fmtMoney(latest.actual_gross_margin)}
              </div>
            </div>
            <div>
              <div style={S.kvLabel}>Variance vs Expected</div>
              <div style={{ color: varianceColor, fontSize: 20, fontWeight: 800, marginTop: 4 }}>
                {latest.variance_amount != null ? `${latest.variance_amount > 0 ? '+' : ''}${fmtMoney(latest.variance_amount)}` : '—'}
              </div>
              <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                {latest.variance_pct != null ? `${Number(latest.variance_pct).toFixed(1)}%` : ''}
                {latest.hit_expected_range && <span style={{ color: GOV_COLORS.success, marginLeft: 6 }}>✓ within L/H range</span>}
                {latest.hit_expected_range === false && <span style={{ color: GOV_COLORS.warning, marginLeft: 6 }}>⚠ outside L/H range</span>}
              </div>
            </div>
          </div>

          {/* Range visualization */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...S.kvLabel, marginBottom: 8 }}>Estimate Range vs Actual</div>
            <div style={{ position: 'relative', height: 40, background: '#0F1923', borderRadius: 6, overflow: 'hidden' }}>
              {/* L-H band */}
              <div style={{
                position: 'absolute', top: 8, height: 24,
                left: `${(low / high) * 100}%`,
                width: `${((high - low) / high) * 100}%`,
                background: GOV_COLORS.accent + '33',
                borderRadius: 4,
                border: `1px solid ${GOV_COLORS.accent}`,
              }} />
              {/* Expected line */}
              <div style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${(expected / high) * 100}%`,
                width: 2, background: GOV_COLORS.accent,
              }} />
              {/* Actual marker */}
              <div style={{
                position: 'absolute', top: 4, bottom: 4,
                left: `${Math.min(100, (latest.actual_total_cost / high) * 100)}%`,
                width: 4, background: varianceColor, borderRadius: 2,
                boxShadow: `0 0 8px ${varianceColor}`,
              }} title={`Actual: ${fmtMoney(latest.actual_total_cost)}`} />
              {/* Labels */}
              <div style={{ position: 'absolute', left: 4, top: 2, color: GOV_COLORS.success, fontSize: 10, fontWeight: 700 }}>{fmtMoney(low)}</div>
              <div style={{ position: 'absolute', right: 4, top: 2, color: GOV_COLORS.warning, fontSize: 10, fontWeight: 700 }}>{fmtMoney(high)}</div>
              <div style={{ position: 'absolute', bottom: 2, left: `${(expected / high) * 100}%`, transform: 'translateX(-50%)', color: GOV_COLORS.accent, fontSize: 10, fontWeight: 700 }}>Expected</div>
            </div>
          </div>

          {latest.notes && (
            <div>
              <div style={S.kvLabel}>Notes</div>
              <div style={{ color: GOV_COLORS.text, fontSize: 13, marginTop: 4, whiteSpace: 'pre-wrap' }}>{latest.notes}</div>
            </div>
          )}

          <div style={{ color: GOV_COLORS.textDim, fontSize: 11, marginTop: 12 }}>
            Imported {new Date(latest.imported_at).toLocaleString()}
          </div>

          {actuals.length > 1 && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ color: GOV_COLORS.textMuted, fontSize: 12, cursor: 'pointer' }}>
                {actuals.length - 1} earlier import{actuals.length > 2 ? 's' : ''}
              </summary>
              <div style={{ marginTop: 8, paddingLeft: 16 }}>
                {actuals.slice(1).map(a => (
                  <div key={a.id} style={{ color: GOV_COLORS.textMuted, fontSize: 11, padding: '4px 0' }}>
                    {new Date(a.imported_at).toLocaleString()} — Total: {fmtMoney(a.actual_total_cost)} · Variance: {a.variance_amount != null ? fmtMoney(a.variance_amount) : '—'}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
